import { useReducer, useEffect, useCallback } from "react";

import Sentry from "@similarweb/sentry-logger";

import { swSettings } from "app/common/services/swSettings";
import {
    getIsRTCreateCampaignsAvailable,
    getMyUserConsumptionMetrics,
} from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignsInfraUtils";
import type { RTContextState } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";
import { ECampaignsLoadingState } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";
import { ERTContextReducerTypes } from "app/pages/digital-marketing/keywords-campaigns/infra/RTContext.consts";
import { RTContextReducer } from "app/pages/digital-marketing/keywords-campaigns/infra/RTContextReducer";
import { useCampaignsQuery } from "app/pages/digital-marketing/keywords-campaigns/shared/hooks";
import { ResponseError } from "app/services/fetchService";

export const useCampaignsState = () => {
    const originalContextState: RTContextState = {
        campaignsConfigurations: null,
        currentCampaignConfiguration: null,
        loadingState: ECampaignsLoadingState.LOADING,
        isLoadingLanguagesAndLocations: true,
        demoCampaigns: [],
        durationObject: null,
        myUserConsumptionMetrics: null,
        locationsAndLanguagesLists: {
            languagesList: [],
            locationsList: [],
        },
        campaignsConsumptionMetrics: {
            usedKeywords: undefined,
            totalKeywords: swSettings.components.RankTracker.resources.NumberTrackedKeywords,
            usedCampaigns: undefined,
            totalCampaigns: swSettings.components.RankTracker.resources.NumberOfCampaigns,
            hasMutualAssetsError: false,
        },
    };

    const [state, dispatch] = useReducer(RTContextReducer, null, () => originalContextState);
    const { data: campaigns, refetch } = useCampaignsQuery({
        enabled: getIsRTCreateCampaignsAvailable(),
        throwOnError: (error) => {
            if (error instanceof ResponseError && error.status) {
                // if we have error status we will show error message otherwise its canceled request due to navigation
                Sentry.errorTracker({
                    message: "Error fetching Keywords campaigns configurations from server",
                    e: error,
                    level: "error",
                });
                dispatch({
                    type: ERTContextReducerTypes.UpdateCampaignsConfigurationsAndLoadingState,
                    payload: {
                        campaignsConfigurations: null,
                        loadingState: ECampaignsLoadingState.ERROR,
                    },
                });
            } else {
                dispatch({
                    type: ERTContextReducerTypes.UpdateLoadingState,
                    payload: { loadingState: ECampaignsLoadingState.DONE },
                });
            }
            return false;
        },
    });

    useEffect(() => {
        if (campaigns && getIsRTCreateCampaignsAvailable()) {
            dispatch({
                type: ERTContextReducerTypes.UpdateCampaignsConfigurations,
                payload: { campaignsConfigurations: campaigns },
            });

            const consumptionResults = getMyUserConsumptionMetrics(campaigns);
            dispatch({
                type: ERTContextReducerTypes.UpdateMyUserConsumptionMetrics,
                payload: { myUserConsumptionMetrics: consumptionResults },
            });
        }
    }, [campaigns]);

    const updateCampaignsFromServer = useCallback(async () => {
        if (!getIsRTCreateCampaignsAvailable()) {
            return;
        }
        await refetch();
    }, [refetch]);

    return {
        state,
        dispatch,
        updateCampaignsFromServer,
    };
};
